/**
 * Who owns a bike, since when, and — for an investor's bike — what share of
 * each week's remittance is theirs.
 *
 * Both change from a date, with a reason. Weeks already charged and not yet
 * paid take the new terms; a week with a payment against it keeps the split it
 * was paid under, and the screen says how many of each.
 */

import { useState, type ReactNode } from 'react';
import {
  BIKE_OWNERSHIP_LABELS,
  SELECTABLE_BIKE_OWNERSHIPS,
  type BikeDetail,
  type BikeOwnership,
} from '@cenox/shared';
import { Button, ErrorState, Input, Select } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { InvestorName } from './parts';
import {
  describeSplitChange,
  useChangeBikeOwnership,
  useInvestorOptions,
  useSetInvestorEntitlement,
} from './investorData';

export function BikeOwnershipPanel({
  bike,
  currency,
  mayChange,
  mayChangeShare,
}: {
  bike: BikeDetail;
  currency: string;
  mayChange: boolean;
  mayChangeShare: boolean;
}): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const [changing, setChanging] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const investorEver = bike.ownership === 'investor' || bike.ownershipHistory.some((p) => p.ownership === 'investor');

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Ownership</p>
        {mayChange && !changing ? (
          <Button size="sm" variant="secondary" onClick={() => setChanging(true)}>
            Change ownership
          </Button>
        ) : null}
      </div>

      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100" aria-label="Ownership history">
        {bike.ownershipHistory.map((p, i) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
            <span className="font-medium text-slate-900">
              {BIKE_OWNERSHIP_LABELS[p.ownership]}
              {p.investorName ? (
                <>
                  {' · '}
                  <InvestorName investorId={p.investorId} name={p.investorName} />
                </>
              ) : p.ownerName ? (
                ` · ${p.ownerName}`
              ) : (
                ''
              )}
              {i === 0 ? <span className="ml-1.5 text-xs font-normal text-emerald-700">current</span> : null}
            </span>
            <span className="text-xs text-slate-500">
              {p.effectiveFrom <= '2000-01-01' ? 'Since registration' : `From ${p.effectiveFrom}`} · {p.reason}
            </span>
          </li>
        ))}
      </ul>

      {changing ? (
        <OwnershipForm
          bike={bike}
          today={today}
          onDone={(message) => {
            setChanging(false);
            setNote(message);
          }}
          onCancel={() => setChanging(false)}
        />
      ) : null}

      {investorEver ? (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Investor’s weekly share</p>
              <p className="mt-0.5 text-sm text-slate-900">
                {bike.currentEntitlement.percent ? (
                  <><span className="font-semibold">{bike.currentEntitlement.percent}%</span> of each week’s remittance</>
                ) : (
                  <><span className="font-semibold">{money(bike.currentEntitlement.weeklyAmount, currency)}</span> a week</>
                )}
                <span className="text-slate-500">
                  {' '}
                  · {bike.currentEntitlement.source === 'agreement' ? 'this bike’s agreement' : 'business default'} ·
                  never more than the week charges; the company keeps the rest
                </span>
              </p>
            </div>
            {mayChangeShare && !sharing ? (
              <Button size="sm" variant="secondary" onClick={() => setSharing(true)}>
                Set agreement
              </Button>
            ) : null}
          </div>
          {bike.entitlements.length > 0 ? (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100" aria-label="Investor share agreements">
              {bike.entitlements.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="font-medium tabular-nums text-slate-900">{e.percent ? `${e.percent}% of remittance` : `${money(e.weeklyAmount, currency)} a week`}</span>
                  <span className="text-xs text-slate-500">
                    From {e.effectiveFrom} · {e.reason}
                    {e.recordedBy ? ` · ${e.recordedBy}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {sharing ? (
            <EntitlementForm
              bike={bike}
              today={today}
              onDone={(message) => {
                setSharing(false);
                setNote(message);
              }}
              onCancel={() => setSharing(false)}
            />
          ) : null}
        </div>
      ) : null}

      {note ? (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
          {note}
        </p>
      ) : null}
    </div>
  );
}

function OwnershipForm({
  bike,
  today,
  onDone,
  onCancel,
}: {
  bike: BikeDetail;
  today: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}): ReactNode {
  const change = useChangeBikeOwnership();
  const investors = useInvestorOptions();
  const [ownership, setOwnership] = useState<BikeOwnership | ''>('');
  const [investorId, setInvestorId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [reason, setReason] = useState('');
  const [weeklyAmount, setWeeklyAmount] = useState(bike.currentEntitlement.weeklyAmount);
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const fields = change.error instanceof ApiError ? change.error.fields : undefined;

  return (
    <form
      aria-label="Change ownership"
      className="space-y-3 rounded-lg bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ownership) return;
        change.mutate(
          {
            bikeId: bike.id,
            input: {
              ownership,
              ...(ownership === 'investor' ? { investorId, ...(weeklyAmount ? { weeklyAmount } : {}) } : {}),
              ...(ownerName && ownership !== 'investor' ? { ownerName } : {}),
              effectiveFrom,
              reason,
            },
          },
          { onSuccess: (r) => onDone(describeSplitChange(r)) },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="New owner"
          required
          value={ownership}
          onChange={(e) => setOwnership(e.target.value as BikeOwnership | '')}
          placeholder="Choose…"
          options={SELECTABLE_BIKE_OWNERSHIPS.map((o) => ({ value: o, label: BIKE_OWNERSHIP_LABELS[o] }))}
          error={fields?.ownership?.[0]}
        />
        {ownership === 'investor' ? (
          <Select
            label="Investor"
            required
            value={investorId}
            onChange={(e) => setInvestorId(e.target.value)}
            placeholder="Choose the investor…"
            options={(investors.data ?? []).map((i) => ({ value: i.id, label: i.name }))}
            error={fields?.investorId?.[0]}
          />
        ) : null}
        {ownership === 'investor' ? (
          <Input
            label={`Investor’s share a week (${currency})`}
            inputMode="decimal"
            value={weeklyAmount}
            onChange={(e) => setWeeklyAmount(e.target.value)}
            hint="The bike’s share agreement from the same date. Never more than the week charges."
            error={fields?.weeklyAmount?.[0]}
          />
        ) : ownership === 'rider' ? (
          <Input label="Rider's name (optional)" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
        ) : (
          <div />
        )}
        <Input
          label="Applies from"
          type="date"
          required
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          hint="Weeks ending on or after this date"
          error={fields?.effectiveFrom?.[0]}
        />
        <Input
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Sold to an investor"
          error={fields?.reason?.[0]}
        />
      </div>
      {change.error && !fields ? <ErrorState message={change.error.message} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={change.isPending || !ownership || !reason.trim() || (ownership === 'investor' && !investorId)}
        >
          Save ownership
        </Button>
      </div>
    </form>
  );
}

function EntitlementForm({
  bike,
  today,
  onDone,
  onCancel,
}: {
  bike: BikeDetail;
  today: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}): ReactNode {
  const set = useSetInvestorEntitlement();
  const [weeklyAmount, setWeeklyAmount] = useState(bike.currentEntitlement.weeklyAmount);
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [reason, setReason] = useState('');
  const fields = set.error instanceof ApiError ? set.error.fields : undefined;

  return (
    <form
      aria-label="Investor share agreement"
      className="space-y-3 rounded-lg bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        set.mutate(
          { bikeId: bike.id, input: { weeklyAmount, effectiveFrom, reason } },
          { onSuccess: (r) => onDone(describeSplitChange(r)) },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label="Investor’s share a week"
          required
          inputMode="decimal"
          value={weeklyAmount}
          onChange={(e) => setWeeklyAmount(e.target.value)}
          error={fields?.weeklyAmount?.[0]}
        />
        <Input
          label="Applies from"
          type="date"
          required
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          error={fields?.effectiveFrom?.[0]}
        />
        <Input
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Agreement signed 1 Nov"
          error={fields?.reason?.[0]}
        />
      </div>
      {set.error && !fields ? <ErrorState message={set.error.message} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={set.isPending || !reason.trim() || !weeklyAmount}>
          Save agreement
        </Button>
      </div>
    </form>
  );
}
